<template>
  <div id="tac-admin-event-source">
    <div class="page-header">
      <h1 class="page-header-title">
        <span class="fa fa-fw fa-film"></span>
        Event Source
      </h1>
      <div class="page-header-actions page-header-actions--right-position">
        <div class="form-inline form-inline--one-liner form-inline--better-spaced form-inline--compact">
          <div class="form-group">
            <search
              v-model="form.filter"
              placeholder="Search for..."
              :disabled="fetchingData"
              @clear="search"
              @execute="search"
              ></search>
          </div>
          <div class="form-group">
            <span class="form-inline__divider" />
          </div>
          <div class="form-group">
            <div class="btn-toolbar">
              <div class="btn-group">
                <button type="button" class="btn btn-default btn-sm" @click="reload" title="Refresh Page" :disabled="fetchingData"><i class="fa fa-fw fa-refresh"></i></button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
      <div class="row">
        <div class="col-lg-4">
          <div class="row">
            <div class="col-lg-12" ref="active-event-grid">
              <table class="table table-bordered table-condensed">
                <caption>Events</caption>
                <thead>
                  <tr>
                    <th class="col-lg-2 text-center">ID</th>
                    <th class="col-lg-10">Name</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="text-center text-info" v-if="!events.length">
                    <td colspan="6">No one is shopping today!</td>
                  </tr>
                  <template v-for="(event, key) in events">
                  <tr :class="{success: isEventActive(event)}" :key="key" @click.stop="onViewEvent(event)" :ref="`active-event-${event.id}`">
                    <td class="text-center">{{ event.id || 'NO ID' }}</td>
                    <td>{{ (event.eventName || 'UNKNOWN NAME') | upperFirst }}</td>
                  </tr>
                  </template>
                </tbody>
                <tfoot>
                  <tr v-show="isPaginationApplicable">
                    <td colspan="12">
                      <pagination
                        class="text-center pagination-compact"
                        :p-total="pagination.total"
                        :p-per-page="pagination.perPage"
                        :p-page="pagination.page"
                        @open-page="openPage"
                        ></pagination>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
        <div class="col-lg-8">
          <div v-if="activeEvent">
            <p>Event Details</p>
            <pre>{{ activeEvent }}</pre>
            <pre>{{ JSON.parse(activeEvent.metadata) }}</pre>
            <pre>{{ JSON.parse(activeEvent.payload) }}</pre>
          </div>
          <p v-else>No event selected!</p>
        </div>
      </div>
    <vue-progress-bar></vue-progress-bar>
  </div><!-- #tac-admin-event-source -->
</template>

<script>
  import _ from 'lodash'
  import { mapState, mapGetters } from 'vuex'
  import moment from 'moment'
  import AuthMixin from '@/plugin/mixin/auth'
  import Pagination from '@/plugin/bootstrap/pagination';
  import Search from '@/plugin/element/search'
  import { upperFirst } from '@/plugin/filter/string'

  const FORM_MODEL = {
    filter: ''
  }

  export default {
    data() {
      return {
        form              : Object.assign({}, FORM_MODEL),
        selection         : {},
        activeEvent       : undefined,
        initialized       : false,
        sorters           : {},
        isEventsBoxActive : false
      }
    },
    created() {
      this.loadFormDataFromRoute()
      this.loadSortersFromRoute()
      this.loadPaginationFromRoute()
      this.updateSpec()
      this.$nextTick(() => {
        this.initialized = true
        this.fetchData()
      })
    },
    watch: {
      events(newVal) {
        if (newVal.length > 0) {
          if (!this.activeEvent) {
            this.viewEvent(_.first(newVal))
          } else if (!_.includes(newVal, this.activeEvent)) {
            this.viewEvent(_.first(newVal))
            this.activateEventsBox()
          }
        }
      },
      isEventsBoxActive(val) {
        if (val) {
          this.createArrowsKeydownListenerForEvent()
          this.createGlobalClickListener()
        } else {
          this.destroyArrowsKeydownListenerForEvent()
          this.destroyGlobalClickListener()
        }
      },
    },
    computed: {
      ...mapState('eventSource', {
        events: 'events',
        pagination: 'pagination',
        fetchingData: 'fetching'
      }),
      ...mapGetters('eventSource', {
        isPaginationApplicable: 'isPaginationApplicable'
      }),
      isFormDefault() {
        return _.isEqual(this.form, FORM_MODEL)
      }
    },
    methods: {
      reload() {
        this.activeEvent = null
        this.fetchData()
      },
      fetchData() {
        this.loadEvents()
      },
      formatDate(date) {
        if (!date || '0000-00-00 00:00:00' === date) {
          return ''
        }

        return moment(date, 'YYYY-MM-DD HH:mm:ss').format('MMM DD, YYYY HH:mm:ss')
      },
      async search() {
        this.updateRoute()
        this.updateSpec()
        await this.loadEvents()
      },
      async loadEvents() {
        if (!this.initialized) {
          return
        }

        try {
          await this.$store.dispatch('eventSource/fetchEvents')
        } catch (e) {
          this.$alertify.alert('Events not loaded')
        }
      },
      loadFormDataFromRoute() {
        const formModelKeys = _.keys(FORM_MODEL)
        const query         = _.get(this.$route, 'query')
        const queryKeys     = _.keys(query)

        _.forEach(queryKeys, (key) => {
          if (!_.includes(formModelKeys, key)) {
            return
          }

          this.form[key] = _.get(this.$route, ['query', key])
        })
      },
      loadSortersFromRoute() {
        const sorterKeys = _.keys(this.sorters)
        const query      = _.get(this.$route, 'query')
        const queryKeys  = _.keys(query)

        _.forEach(queryKeys, (key) => {
          if (!_.includes(sorterKeys, key)) {
            return
          }

          this.sorters[key] = _.get(this.$route, ['query', key])
        })
      },
      loadPaginationFromRoute() {
        const page = parseInt(this.$route.query.page)
        if (page && 1 !== page) {
          this.$store.dispatch('eventSource/changePage', page)
        }
        const perPage = parseInt(this.$route.query.perPage)
        if (perPage && 10 !== perPage) {
          this.$store.dispatch('eventSource/changeItemsPerPage', perPage)
        }
      },
      updateRoute() {
        let spec = Object.assign({}, this.sorters)
        if (!this.isFormDefault) {
          spec = Object.assign({}, spec, this.form)
        }

        spec = _.omitBy(spec, _.isNull)
        spec = _.omitBy(spec, v => !v)

        this.$router.push({
          name: 'admin/event-source',
          query: spec
        })
      },
      clearAllSorters() {
      },
      updateSpec() {
        let spec = Object.assign({}, this.form, this.sorters)

        spec = _.omitBy(spec, _.isNull)
        spec = _.omitBy(spec, v => !v)

        this.$store.dispatch('eventSource/updateSpec', spec)
      },
      createGlobalClickListener() {
        window.addEventListener('click', this.onGlobalClickListener)
      },
      destroyGlobalClickListener() {
        window.removeEventListener('click', this.onGlobalClickListener)
      },
      onGlobalClickListener() {
        this.deactivateEventsBox()
      },
      createArrowsKeydownListenerForEvent() {
        window.addEventListener('keydown', this.onGlobalArrowsKeydownForEvent)
      },
      destroyArrowsKeydownListenerForEvent() {
        window.removeEventListener('keydown', this.onGlobalArrowsKeydownForEvent)
      },
      onGlobalArrowsKeydownForEvent(e) {
        if (true === e.ctrlKey || true === e.shiftKey  || true === e.altKey) {
          return
        }
        if ('ArrowUp' !== e.code && 'ArrowDown' !== e.code) {
          return
        }

        e.preventDefault()
        e.stopPropagation()

        if ('ArrowUp' === e.code) {
          this.selectPreviousEvent()
          this.scrollIntoViewEvent('up')
        } else if ('ArrowDown' === e.code) {
          this.selectNextEvent()
          this.scrollIntoViewEvent('down')
        }
      },
      selectNextEvent() {
        let event = this.nextEvent()
        this.viewEvent(event)
      },
      selectPreviousEvent() {
        let event = this.previousEvent()
        this.viewEvent(event)
      },
      nextEvent() {
        let index = _.indexOf(this.events, this.activeEvent)
        if (-1 === index) {
          return null
        }

        let nextIndex = index + 1
        if (nextIndex > this.events.length - 1) {
          nextIndex = this.events.length - 1
        }

        return _.nth(this.events, nextIndex)
      },
      previousEvent() {
        let index = _.indexOf(this.events, this.activeEvent)
        if (-1 === index) {
          return null
        }

        let nextIndex = index > 0 ? index - 1 : 0
        if (nextIndex > this.events.length) {
          nextIndex = this.events.length - 1
        }

        return _.nth(this.events, nextIndex)
      },
      onViewEvent(event) {
        this.activateEventsBox()
        this.viewEvent(event)
      },
      viewEvent(event) {
        this.activeEvent = event
      },
      isEventActive(event) {
        return this.activeEvent === event
      },
      onOpenPage(page) {
        this.deactivateEventsBox()
        this.openPage(page)
      },
      openPage(page) {
        this.$store.dispatch('eventSource/changePage', page)

        let query = _.assign({}, this.$route.query, {page: page})
        if (query.page && 1 == query.page) {
          delete query.page
        }
        if (query.itemsPerPage && 10 == query.itemsPerPage) {
          delete query.itemsPerPage
        }

        this.$router.push({
          name   : this.$route.name,
          params : _.assign({}, this.$route.params),
          query  : _.assign({}, query),
        })

        this.fetchData()
      },
      activateEventsBox() {
        this.isEventsBoxActive = true
      },
      deactivateEventsBox() {
        this.isEventsBoxActive = false
      },
      scrollIntoViewEvent(direction) {
        const rowRef = this.$refs[`active-event-${this.activeEvent.id}`]
        if (!rowRef) {
          return
        }

        const gridRef = this.$refs['active-event-grid']
        if (!gridRef) {
          return
        }

        const rowEl = rowRef[0]
        const rowRect = rowEl.getBoundingClientRect()

        const gridEl = gridRef
        const gridRect = gridEl.getBoundingClientRect()

        let position = rowRect.top - gridRect.top
        if ('down' === direction) {
          position = position + rowRect.height
        }

        if (position > 0 && position <= gridRect.height) {
          return
        }

        rowEl.scrollIntoView()
      }
    },
    components: {
      Pagination,
      Search
    },
    filters: {
      upperFirst
    },
    mixins: [
      AuthMixin
    ]
  }
</script>
